import React, { useEffect } from 'react';
import { Link } from '@inertiajs/react';
import formatDuration from '../../utils/formatDuration';
import type { Project } from '../../types/project';
import type { TodoCounts, TodoFacts, TodoView } from '../../types/todo';

type SidebarProps = {
  view: TodoView;
  projectId: number | null;
  counts: TodoCounts;
  projects: Project[];
  facts: TodoFacts;
  today: string;
  open: boolean;
  onClose: () => void;
  // 把某一行的清单扔给 AI。传的是「哪一个清单」而不是「当前那个」——
  // 左栏是导航，它天然表达的就是前者，于是你在「今天」也能把待办池扔出去。
  onHandoffList: (view: TodoView, projectId: number | null, name: string, count: number) => void;
  // 左栏这个搜索框。与中栏那个是同一次搜索的两个镜子 —— 值和回调都由页面
  // 持有，两处必须同源。留两个的理由是可达范围不同：这一个在**任何视图**
  // 里都在（在「今天」想搜不用先切走），中栏那个只有结果那一栏才有。
  searchBox: {
    draft: string;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onChange: (value: string) => void;
    onClear: () => void;
  };
};

// 「近期」不显示计数（原型也不显示）：它装的是「未完成的全部」，那个数字
// 大到没有行动含义，只会变成又一个让人焦虑的读数。
const SMART_LISTS: { key: Exclude<TodoView, 'project'>; icon: string; name: string; counted: boolean }[] = [
  { key: 'today', icon: '☀', name: '今天', counted: true },
  { key: 'tomorrow', icon: '☾', name: '明天', counted: true },
  { key: 'important', icon: '★', name: '重要', counted: true },
  { key: 'backlog', icon: '≡', name: '待办池', counted: true },
  { key: 'recent', icon: '◇', name: '近期', counted: false },
  // 与「近期」同样不显示计数：回收站的条数不是需要时刻盯着的读数，
  // 而且 TodoCounts 里根本没有这个键，标 true 会渲染出 undefined。
  { key: 'trash', icon: '🗑', name: '回收站', counted: false },
];

const ROW = 'flex w-full items-center gap-[9px] rounded-[7px] px-[9px] py-[7px] text-left text-[13.5px]';

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

function formatToday(iso: string): string {
  // 后端给的是 YYYY-MM-DD。补上 T00:00:00 走本地时间解析，
  // 直接 new Date('2026-08-18') 会被当成 UTC 午夜，在 UTC+8 之外的时区会差一天。
  const date = new Date(`${iso}T00:00:00`);
  return `${date.getMonth() + 1}月${date.getDate()}日 ${WEEKDAYS[date.getDay()]}`;
}

const Sidebar: React.FC<SidebarProps> = ({
  view, projectId, counts, projects, facts, today, open, onClose, onHandoffList, searchBox,
}) => {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const rowClass = (on: boolean) =>
    on
      ? `${ROW} bg-todo-card font-semibold text-todo-ink shadow-sm`
      : `${ROW} text-todo-ink2 hover:bg-todo-card2`;

  return (
    <>
      {/* 遮罩只在窄屏且抽屉打开时存在。sm: 之后侧栏是静态列，不需要遮罩。 */}
      {open && (
        <div
          onClick={onClose}
          className="fixed inset-0 z-20 bg-black/30 sm:hidden"
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          // base（< 640px）：固定定位的抽屉，关闭时移出视口左侧。
          // 只用 translate 不够——移出视口的链接仍然能被 Tab 聚焦，
          // 等于把一堆看不见的焦点塞进 tab 序列。所以关闭时同时 invisible。
          'fixed inset-y-0 left-0 z-30 w-[264px] transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full invisible',
          // sm: 之后完全恢复原来的静态网格列（visible 必须显式写回来，
          // 否则窄屏关闭状态下切到宽屏，侧栏会是隐形的）
          'sm:visible sm:static sm:z-auto sm:w-auto sm:translate-x-0 sm:transition-none',
          'flex min-h-0 flex-col border-r border-todo-line bg-todo-side',
        ].join(' ')}
      >
      <div className="px-[14px] pb-[10px] pt-[14px]">
        <div className="text-[15px] font-semibold tracking-tight">
          asaptodo
          <em className="ml-[6px] text-[12px] font-normal not-italic text-todo-muted">
            {formatToday(today)}
          </em>
        </div>

        {/* 就是三栏原型（todo-ui-mockup-3pane.html:281）里那个框。它当年因为
            「点了没反应」被整个拿掉，现在有反应了。
            ⌘K 送的就是这一个 —— 不开任何浮层。 */}
        <label className="mt-[11px] flex items-center gap-[7px] rounded-[7px] border border-todo-line bg-todo-card px-[9px] py-1.5 focus-within:border-todo-accent">
          <span aria-hidden="true" className="text-[12px] leading-none text-todo-muted">🔍</span>
          <input
            ref={searchBox.inputRef}
            value={searchBox.draft}
            onChange={(event) => searchBox.onChange(event.target.value)}
            placeholder="搜索"
            aria-label="搜索任务"
            className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-todo-ink outline-none placeholder:text-todo-muted"
          />
          {/* 有词时给出口，没词时给键位 —— 这个框太窄，两个并排会挤掉输入区。 */}
          {searchBox.draft ? (
            <button
              type="button"
              onClick={searchBox.onClear}
              title="清除搜索词"
              aria-label="清除搜索词"
              className="flex-none px-0.5 text-[11px] text-todo-muted hover:text-todo-ink"
            >
              ✕
            </button>
          ) : (
            <kbd className="flex-none rounded border border-todo-line bg-todo-card2 px-1 font-mono text-[10px] leading-[16px] text-todo-muted">
              ⌘K
            </kbd>
          )}
        </label>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-1">
        {SMART_LISTS.map(({ key, icon, name, counted }) => {
          const on = view === key;
          const count = counted ? counts[key] : 0;

          // 回收站没有清单级动作：那一栏只有「恢复 / 彻底删除」。
          const handoffable = key !== 'trash';

          return (
            <div key={key} className="group relative">
              <Link href={`/todo/${key}`} onClick={onClose} className={rowClass(on)}>
                <span
                  className={`w-4 flex-none text-center text-[13px] ${on ? 'text-todo-accent' : 'text-todo-muted'}`}
                >
                  {icon}
                </span>
                <span className="min-w-0 flex-1 truncate">{name}</span>
                {count > 0 && <span className="text-[11.5px] tabular-nums text-todo-muted">{count}</span>}
                {/* 给 ✦ 预留固定宽度：不预留的话计数会在 hover 时左移一下。 */}
                {handoffable && <span className="w-4 flex-none" aria-hidden="true" />}
              </Link>
              {/* 兄弟节点而不是塞进 <Link> 里 —— <button> 套在 <a> 里是非法
                  HTML，浏览器行为不一致。窄屏是抽屉、没有 hover，所以 sm: 之后
                  才渲染。 */}
              {handoffable && (
                <button
                  type="button"
                  onClick={() => onHandoffList(key, null, name, count)}
                  aria-label={`把「${name}」扔给 AI`}
                  className="absolute right-[9px] top-1/2 hidden -translate-y-1/2 text-[13px] text-todo-muted opacity-0 hover:text-todo-star focus-visible:opacity-100 group-hover:opacity-100 sm:block"
                >
                  ✦
                </button>
              )}
            </div>
          );
        })}

        <div className="mx-[9px] my-2 h-px bg-todo-line" />
        <div className="px-[9px] pb-[3px] pt-1 text-[11px] tracking-wide text-todo-muted">项目</div>

        {projects.map((project) => {
          // 键是字符串：后端用整数 project_id 做键，to_json 会转成字符串
          const count = counts.projects[String(project.id)] ?? 0;

          return (
            <div key={project.id} className="group relative">
              <Link
                href={`/todo/projects/${project.id}`}
                onClick={onClose}
                className={rowClass(view === 'project' && projectId === project.id)}
              >
                <span className="mx-1 h-2 w-2 flex-none rounded-full" style={{ background: project.color }} />
                <span className="min-w-0 flex-1 truncate">{project.name}</span>
                {count > 0 && <span className="text-[11.5px] tabular-nums text-todo-muted">{count}</span>}
                <span className="w-4 flex-none" aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={() => onHandoffList('project', project.id, project.name, count)}
                aria-label={`把项目「${project.name}」扔给 AI`}
                className="absolute right-[9px] top-1/2 hidden -translate-y-1/2 text-[13px] text-todo-muted opacity-0 hover:text-todo-star focus-visible:opacity-100 group-hover:opacity-100 sm:block"
              >
                ✦
              </button>
            </div>
          );
        })}
      </nav>

      {/* 只放「已经发生的事实」。不放完成率、连续天数、落后多少——那些一造出来
          就会被优化成噪音（spec §4.2）。 */}
      <div className="border-t border-todo-line px-3 pb-[11px] pt-[9px]">
        <div className="flex gap-[13px]">
          <div>
            <b className="block text-[14px] font-semibold leading-tight tabular-nums">
              {formatDuration(Math.round(facts.focused_seconds / 60))}
            </b>
            <small className="text-[10.5px] text-todo-muted">今天已专注</small>
          </div>
          <div>
            <b className="block text-[14px] font-semibold leading-tight tabular-nums">
              {facts.pomodoro_count}
            </b>
            <small className="text-[10.5px] text-todo-muted">番茄</small>
          </div>
          {/* 清单级的产出落在 worklog，而这个计数不指向具体哪一条。做成可点
              跳到列表，把那个洞补上 —— 零迁移，一个链接。 */}
          <Link href="/worklog" className="hover:text-todo-accent">
            <b className="block text-[14px] font-semibold leading-tight tabular-nums">
              {facts.worklog_count}
            </b>
            <small className="text-[10.5px] text-todo-muted">agent 日志</small>
          </Link>
        </div>
      </div>
      </aside>
    </>
  );
};

export default Sidebar;
